import { LocationQuery, useRouter as useBaseRouter } from 'vue-router'
import { CompanyServiceRoute } from '@/views/company/service/routes'
import { CompanyAssetRouteName } from '@/views/company/asset/routes'
import {
    articleParamKey,
    assetParamKey,
    bankFeedParamKey,
    categoryParamKey,
    companyLocationParamKey,
    companyParamKey,
    contactParamKey,
    csParamKey,
    csUsageParamKey,
    invoiceItemParamKey,
    invoiceParamKey,
    logParamKey,
    paymentItemParamKey,
    paymentParamKey,
    productParamKey,
    secretParamKey,
    staffParamKey,
    tenantParamKey,
    ticketDepartmentParamKey,
    ticketParamKey,
    ticketPriorityParamKey,
    ticketTypeParamKey,
    useRoute,
} from '@/composables/route'
import { CompanySecretRouteName } from '@/views/company/secret/routes'
import { CompanyLocationRouteName } from '@/views/company/location/routes'
import { CompanyContactRouteName } from '@/views/company/contact/routes'
import { CompanyLogRouteName } from '@/views/company/log/routes'
import {
    AssetRouteName,
    CompanyRouteName,
    ContactRouteName,
    LocationRouteName,
} from '@/views/company/routes'
import { CompanyInvoiceRouteName } from '@/views/company/invoice/routes'
import { CompanyReceiptRouteName } from '@/views/company/receipt/routes'
import { CompanyStatementRouteName } from '@/views/company/statement/routes'
import { CompanyBillingRouteName } from '@/views/company/billing/routes'
import { CompanyPayRouteName } from '@/views/company/pay/routes'
import { AdminStaffRouteName } from '@/views/admin/staff/routes'
import { AdminTenantsRouteName } from '@/views/admin/tenant/routes'
import { CompanySendRoute } from '@/views/company/send/routes'
import { TicketRouteName } from '@/modules/ticket/routes'
import { ServiceRouteName } from '@/views/company-services/routes'
import { CompanyTicketRouteName } from '@/views/company/ticket/routes'
import { ArticleRouteName } from '@/views/article/routes'
import { CompanyArticleRouteName } from '@/views/company/article/routes'
import { ProductRouteName } from '@/views/sales/product/routes'
import { BankFeedRouteName } from '@/views/finance/bank_feed/routes'
import { CategoryRouteName } from '@/views/system/category/routes'
import { TicketTypeRouteName } from '@/modules/ticket-type/routes'
import { TicketPriorityRouteName } from '@/modules/ticket-priority/routes'
import { TicketDepartmentRouteName } from '@/modules/ticket-department/routes'
import { FinanceInvoiceRouteName } from '@/views/finance/invoices/routes'
import { ReceivableRouteName } from '@/views/finance/receivables/routes'

export const useRouter = () => {
    const route = useRoute()
    const router = useBaseRouter()

    const goToCompanyLogs = (companyUuid?: string | null) =>
        router.push({
            name: CompanyLogRouteName.Index,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyLogDetails = (logId: number, companyUuid?: string | null) =>
        router.push({
            name: CompanyLogRouteName.Show,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanySecrets = (companyUuid?: string | null) =>
        router.push({
            name: CompanySecretRouteName.Index,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanySecretsCreate = (companyUuid?: string | null) =>
        router.push({
            name: CompanySecretRouteName.Create,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanySecretEdit = (uuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanySecretRouteName.Edit,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [secretParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToCompanySecretDetails = (uuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanySecretRouteName.Show,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [secretParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToCompanySecretLogs = (uuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanySecretRouteName.Logs,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [secretParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToCompanySecretLogDetails = (
        logId: number,
        secretUuid?: string | null,
        companyUuid?: string | null
    ) =>
        router.push({
            name: CompanySecretRouteName.LogDetails,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [secretParamKey]: secretUuid ?? route.secretUuid.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanyServices = (companyUuid?: string | null) =>
        router.push({
            name: CompanyServiceRoute.Index,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyServiceDetails = (uuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanyServiceRoute.Details,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToCompanyServiceEdit = (uuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanyServiceRoute.Edit,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToCompanyServiceEditRate = (uuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanyServiceRoute.EditRate,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToCompanyServiceClone = (uuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanyServiceRoute.Clone,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToCompanyCompanyServiceUsageDetails = (
        uuid: string,
        csUuid?: string | null,
        companyUuid?: string | null
    ) =>
        router.push({
            name: CompanyServiceRoute.UsageDetails,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
                [csUsageParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToCompanyServiceCreate = () => {
        router.push({
            name: CompanyServiceRoute.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToCompanyServiceLogs = (csUuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanyServiceRoute.Logs,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: csUuid,
            },
            query: route.query.value,
        })

    const goToCompanyServiceLogDetails = (
        logId: number,
        csUuid?: string | null,
        companyUuid?: string | null
    ) =>
        router.push({
            name: CompanyServiceRoute.LogDetails,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanyServiceInvoices = (csUuid?: string | null, companyUuid?: string | null) =>
        router.push({
            name: CompanyServiceRoute.Invoices,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyServiceUsages = (csUuid?: string | null, companyUuid?: string | null) =>
        router.push({
            name: CompanyServiceRoute.Usage,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyServiceInvoiceEdit = (
        invoiceUuid: string,
        csUuid?: string | null,
        companyUuid?: string | null
    ) =>
        router.push({
            name: CompanyServiceRoute.InvoiceEdit,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
                [invoiceItemParamKey]: invoiceUuid,
            },
            query: route.query.value,
        })

    const goToCompanyServiceInvoiceLogs = (
        invoiceUuid: string,
        csUuid?: string | null,
        companyUuid?: string | null
    ) =>
        router.push({
            name: CompanyServiceRoute.InvoiceLogs,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
                [invoiceItemParamKey]: invoiceUuid,
            },
            query: route.query.value,
        })

    const goToCompanyAssets = () =>
        router.push({
            name: CompanyAssetRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToCompanyAssetCreate = () =>
        router.push({
            name: CompanyAssetRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })

    const goToCompanyAssetDetails = (assetUuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanyAssetRouteName.Show,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [assetParamKey]: assetUuid,
            },
            query: route.query.value,
        })

    const goToCompanyAssetEdit = (assetUuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanyAssetRouteName.Edit,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [assetParamKey]: assetUuid,
            },
            query: route.query.value,
        })

    const goToCompanyAssetLogs = (assetUuid: string, companyUuid?: string | null) =>
        router.push({
            name: CompanyAssetRouteName.Logs,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [assetParamKey]: assetUuid,
            },
            query: route.query.value,
        })

    const goToCompanyAssetLogDetails = (logId: number) =>
        router.push({
            name: CompanyAssetRouteName.LogDetails,
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanyAssetSecrets = (assetUuid?: string | null, companyUuid?: string | null) =>
        router.push({
            name: CompanyAssetRouteName.Secrets,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyAssetSecretCreate = (assetUuid?: string | null, companyUuid?: string | null) =>
        router.push({
            name: CompanyAssetRouteName.SecretCreate,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyAssetSecretEdit = (
        secretUuid: string,
        assetUuid?: string | null,
        companyUuid?: string | null
    ) =>
        router.push({
            name: CompanyAssetRouteName.SecretEdit,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
                [secretParamKey]: secretUuid,
            },
            query: route.query.value,
        })

    const goToCompanyAssetSecretDetails = (secretUuid: string) =>
        router.push({
            name: CompanyAssetRouteName.SecretDetails,
            params: {
                ...route.params.value,
                [secretParamKey]: secretUuid,
            },
            query: route.query.value,
        })

    const goToCompanyAssetSecretLogs = (
        secretUuid: string,
        assetUuid?: string | null,
        companyUuid?: string | null
    ) =>
        router.push({
            name: CompanyAssetRouteName.SecretLogs,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
                [secretParamKey]: secretUuid,
            },
            query: route.query.value,
        })

    const goToCompanyAssetSecretLogDetails = (
        logId: number,
        secretUuid: string | null,
        assetUuid?: string | null,
        companyUuid?: string | null
    ) =>
        router.push({
            name: CompanyAssetRouteName.SecretLogDetails,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
                [secretParamKey]: secretUuid ?? route.secretUuid.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanyBillingMultiple = (companyUuid?: string, query?: LocationQuery) =>
        router.push({
            name: CompanyBillingRouteName.Multiple,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyBillingSingle = (companyUuid?: string, query?: LocationQuery) =>
        router.push({
            name: CompanyBillingRouteName.Single,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyBillingProduct = (companyUuid?: string, query?: LocationQuery) =>
        router.push({
            name: CompanyBillingRouteName.Product,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyBillingMultipleSend = (
        invoiceUuid: string,
        companyUuid?: string,
        query?: LocationQuery
    ) =>
        router.push({
            name: CompanyBillingRouteName.MultipleSend,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [invoiceParamKey]: invoiceUuid,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyBillingCombine = (companyUuid?: string, query?: LocationQuery) =>
        router.push({
            name: CompanyBillingRouteName.Combine,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyBillingCombineEditInvoiceItem = (
        invoiceItemUuid: string,
        companyUuid?: string,
        query?: LocationQuery
    ) =>
        router.push({
            name: CompanyBillingRouteName.CombineEditInvoiceItem,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [invoiceItemParamKey]: invoiceItemUuid,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyBillingCombineSend = (
        invoiceUuid: string,
        companyUuid?: string,
        query?: LocationQuery
    ) =>
        router.push({
            name: CompanyBillingRouteName.CombineSend,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [invoiceParamKey]: invoiceUuid,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyPay = (companyUuid?: string, query?: LocationQuery) =>
        router.push({
            name: CompanyPayRouteName.Index,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyPayEditInvoice = (
        invoiceUuid: string,
        companyUuid?: string,
        query?: LocationQuery
    ) =>
        router.push({
            name: CompanyPayRouteName.EditInvoice,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [invoiceParamKey]: invoiceUuid,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyPaySendReceipt = (
        receiptUuid: string,
        companyUuid?: string,
        query?: LocationQuery
    ) =>
        router.push({
            name: CompanyPayRouteName.SendReceipt,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [paymentParamKey]: receiptUuid,
            },
            query: {
                ...route.query.value,
                ...query,
            },
        })

    const goToCompanyContacts = (companyUuid?: string) =>
        router.push({
            name: CompanyContactRouteName.Index,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyContactCreate = () =>
        router.push({
            name: CompanyContactRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })

    const goToCompanyContactLogs = (contactUuid?: string) => {
        router.push({
            name: CompanyContactRouteName.Logs,
            params: {
                ...route.params.value,
                [contactParamKey]: contactUuid ?? route.companyContactUuid.value,
            },
            query: route.query.value,
        })
    }

    const goToCompanyContactDetails = (contactUuid: string, companyUuid?: string | null) => {
        if (
            contactUuid === route.companyContactUuid.value &&
            route.name.value === CompanyContactRouteName.Show
        ) {
            goToCompanyContacts().then((r) => {})
            return
        }

        router
            .push({
                name: CompanyContactRouteName.Show,
                params: {
                    ...route.params.value,
                    [companyParamKey]: companyUuid ?? route.companyUuid.value,
                    [contactParamKey]: contactUuid,
                },
                query: route.query.value,
            })
            .then((r) => {})
    }

    const goToCompanyContactEdit = (contactUuid: string) =>
        router.push({
            name: CompanyContactRouteName.Edit,
            params: {
                ...route.params.value,
                [contactParamKey]: contactUuid,
            },
            query: route.query.value,
        })

    const goToCompanyContactResetPassword = (contactUuid: string) =>
        router.push({
            name: CompanyContactRouteName.ResetPassword,
            params: {
                ...route.params.value,
                [contactParamKey]: contactUuid,
            },
            query: route.query.value,
        })

    const goToCompanyContactLogDetails = (logId: number) =>
        router.push({
            name: CompanyContactRouteName.LogDetails,
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanyDashboard = (companyUuid?: string) =>
        router.push({
            name: CompanyRouteName.Dashboard,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyInvoices = (companyUuid?: string, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.Index,
            finance: FinanceInvoiceRouteName.Index,
        }

        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoiceEdit = (invoiceUuid: string, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.Edit,
            finance: FinanceInvoiceRouteName.Edit,
        }

        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [invoiceParamKey]: invoiceUuid,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoiceDetails = (
        invoiceUuid?: string,
        companyUuid?: string | null,
        parentKey?: string
    ) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.Show,
            finance: FinanceInvoiceRouteName.Show,
        }
        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [invoiceParamKey]: invoiceUuid ?? route.invoiceUuid.value,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoiceLogs = (invoiceUuid?: string, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.Logs,
            finance: FinanceInvoiceRouteName.Logs,
        }
        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [invoiceParamKey]: invoiceUuid ?? route.invoiceUuid.value,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoiceLogDetails = (logId: number, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.LogDetails,
            finance: FinanceInvoiceRouteName.LogDetails,
        }

        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoicePayments = (invoiceUuid?: string, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.Payments,
            finance: FinanceInvoiceRouteName.Payments,
        }
        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [invoiceParamKey]: invoiceUuid ?? route.invoiceUuid.value,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoicePaymentDetails = (paymentItemUuid: number, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.PaymentDetails,
            finance: FinanceInvoiceRouteName.PaymentDetails,
        }
        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [paymentItemParamKey]: paymentItemUuid,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoiceSend = (invoiceUuid: string, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.Send,
            finance: FinanceInvoiceRouteName.Send,
        }
        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [invoiceParamKey]: invoiceUuid,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoiceItemEdit = (itemUuid: string, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.EditItem,
            finance: FinanceInvoiceRouteName.EditItem,
        }
        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [invoiceItemParamKey]: itemUuid,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoiceItemLogs = (itemUuid: string, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.ItemLogs,
            finance: FinanceInvoiceRouteName.ItemLogs,
        }
        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [invoiceItemParamKey]: itemUuid,
            },
            query: route.query.value,
        })
    }

    const goToCompanyInvoiceItemLogDetails = (logId: number, parentKey?: string) => {
        const invoiceRoute = {
            company: CompanyInvoiceRouteName.ItemLogDetails,
            finance: FinanceInvoiceRouteName.ItemLogDetails,
        }
        router.push({
            name: invoiceRoute[parentKey ?? 'company'],
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })
    }

    const goToCompanySendEmail = (companyUuid: string) =>
        router.push({
            name: CompanySendRoute.SendEmail,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid,
            },
            query: route.query.value,
        })

    const goToCompanyTickets = (companyUuid?: string) =>
        router.push({
            name: CompanyTicketRouteName.Index,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyTicketEdit = (ticketUuid?: string) =>
        router.push({
            name: CompanyTicketRouteName.Edit,
            params: {
                ...route.params.value,
                [ticketParamKey]: ticketUuid ?? route.ticketUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyTicketLogs = (ticketUuid?: string) =>
        router.push({
            name: CompanyTicketRouteName.Logs,
            params: {
                ...route.params.value,
                [ticketParamKey]: ticketUuid ?? route.ticketUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyTicketLogDetails = (logId: number, ticketUuid?: string) =>
        router.push({
            name: CompanyTicketRouteName.LogDetails,
            params: {
                ...route.params.value,
                [ticketParamKey]: ticketUuid ?? route.ticketUuid.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanyReceipts = (companyUuid?: string) =>
        router.push({
            name: CompanyReceiptRouteName.Index,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyReceiptDetails = (paymentUuid?: string) =>
        router.push({
            name: CompanyReceiptRouteName.Show,
            params: {
                ...route.params.value,
                [paymentParamKey]: paymentUuid ?? route.paymentUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyReceiptEdit = (paymentUuid: string) =>
        router.push({
            name: CompanyReceiptRouteName.Edit,
            params: {
                ...route.params.value,
                [paymentParamKey]: paymentUuid,
            },
            query: route.query.value,
        })

    const goToCompanyReceiptLogs = (paymentUuid?: string) =>
        router.push({
            name: CompanyReceiptRouteName.Logs,
            params: {
                ...route.params.value,
                [paymentParamKey]: paymentUuid ?? route.paymentUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyReceiptLogDetails = (logId: number) =>
        router.push({
            name: CompanyReceiptRouteName.LogDetails,
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanyReceiptSend = (paymentUuid: string) =>
        router.push({
            name: CompanyReceiptRouteName.Send,
            params: {
                ...route.params.value,
                [paymentParamKey]: paymentUuid,
            },
            query: route.query.value,
        })

    const goToCompanyReceiptItemEdit = (itemUuid: string) =>
        router.push({
            name: CompanyReceiptRouteName.EditItem,
            params: {
                ...route.params.value,
                [paymentItemParamKey]: itemUuid,
            },
            query: route.query.value,
        })

    const goToCompanyReceiptItemLogs = (itemUuid: string) =>
        router.push({
            name: CompanyReceiptRouteName.ItemLogs,
            params: {
                ...route.params.value,
                [paymentItemParamKey]: itemUuid,
            },
            query: route.query.value,
        })

    const goToCompanyReceiptItemLogDetails = (logId: number) =>
        router.push({
            name: CompanyReceiptRouteName.ItemLogDetails,
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanyLocations = (companyUuid?: string) =>
        router.push({
            name: CompanyLocationRouteName.Index,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyLocationCreate = (companyUuid?: string) =>
        router.push({
            name: CompanyLocationRouteName.Create,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyLocationLogs = (locationUuid?: string) =>
        router.push({
            name: CompanyLocationRouteName.Logs,
            params: {
                ...route.params.value,
                [companyLocationParamKey]: locationUuid ?? route.companyLocationUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyLocationLogDetails = (logId: number) =>
        router.push({
            name: CompanyLocationRouteName.LogDetails,
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanyLocationEdit = (locationUuid: string) =>
        router.push({
            name: CompanyLocationRouteName.Edit,
            params: {
                ...route.params.value,
                [companyLocationParamKey]: locationUuid,
            },
            query: route.query.value,
        })

    const goToCompanyStatements = (companyUuid?: string) =>
        router.push({
            name: CompanyStatementRouteName.Index,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToCompanyStatementNotes = (companyUuid?: string) =>
        router.push({
            name: CompanyStatementRouteName.Notes,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
            },
            query: route.query.value,
        })

    const goToAdminStaffs = (staffUuid?: string) => {
        const staffQuery = Object.fromEntries(
            Object.entries(route.query.value).filter(([key]) => key.startsWith('staffs'))
        )

        router.push({
            name: AdminStaffRouteName.Index,
            params: {
                ...route.params.value,
                [staffParamKey]: staffUuid ?? route.staffUuid.value,
            },
            query: staffQuery,
        })
    }

    const goToAdminStaffCreate = () => {
        router.push({
            name: AdminStaffRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToAdminStaffEdit = (staffUuid?: string) => {
        router.push({
            name: AdminStaffRouteName.Edit,
            params: {
                ...route.params.value,
                [staffParamKey]: staffUuid ?? route.staffUuid.value,
            },
            query: route.query.value,
        })
    }

    const goToAdminStaffActivities = (staffUuid?: string) => {
        router.push({
            name: AdminStaffRouteName.Activities,
            params: {
                ...route.params.value,
                [staffParamKey]: staffUuid ?? route.staffUuid.value,
            },
            query: route.query.value,
        })
    }

    const goToAdminStaffActivityDetails = (logId: number) =>
        router.push({
            name: AdminStaffRouteName.Details,
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToCompanies = () => {
        router.push({
            name: CompanyRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToCompanyCreate = () => {
        router.push({
            name: CompanyRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToContactCreate = () =>
        router.push({
            name: ContactRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })

    const goToContactUpdate = (contactUuid: string) =>
        router.push({
            name: ContactRouteName.Edit,
            params: {
                ...route.params.value,
                [contactParamKey]: contactUuid ?? route.companyContactUuid.value,
            },
            query: route.query.value,
        })

    const goToContacts = () =>
        router.push({
            name: ContactRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToContactDetails = (contactUuid: string, companyUuid?: string | null) => {
        router.push({
            name: ContactRouteName.Show,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid,
                [contactParamKey]: contactUuid,
            },
            query: route.query.value,
        })
    }
    const goToContactLogs = (contactUuid?: string) => {
        router.push({
            name: ContactRouteName.Logs,
            params: {
                ...route.params.value,
                [contactParamKey]: contactUuid,
            },
            query: route.query.value,
        })
    }

    const goToContactLogDetails = (logId: number) =>
        router.push({
            name: ContactRouteName.LogDetails,
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToContactResetPassword = (contactUuid: string) =>
        router.push({
            name: ContactRouteName.ResetPassword,
            params: {
                ...route.params.value,
                [contactParamKey]: contactUuid,
            },
            query: route.query.value,
        })

    const goToLocations = () => {
        router.push({
            name: LocationRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToLocationCreate = () =>
        router.push({
            name: LocationRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })

    const goToLocationLogs = (locationUuid?: string) =>
        router.push({
            name: LocationRouteName.Logs,
            params: {
                ...route.params.value,
                [companyLocationParamKey]: locationUuid,
            },
            query: route.query.value,
        })

    const goToLocationLogDetails = (logId: number) =>
        router.push({
            name: LocationRouteName.LogDetails,
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToLocationEdit = (locationUuid: string) =>
        router.push({
            name: LocationRouteName.Edit,
            params: {
                ...route.params.value,
                [companyLocationParamKey]: locationUuid,
            },
            query: route.query.value,
        })

    const goToAssets = () =>
        router.push({
            name: AssetRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToAssetCreate = () =>
        router.push({
            name: AssetRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })

    const goToAssetDetails = (assetUuid: string) =>
        router.push({
            name: AssetRouteName.Show,
            params: {
                ...route.params.value,
                [assetParamKey]: assetUuid,
            },
            query: route.query.value,
        })

    const goToAssetEdit = (assetUuid: string) =>
        router.push({
            name: AssetRouteName.Edit,
            params: {
                ...route.params.value,
                [assetParamKey]: assetUuid,
            },
            query: route.query.value,
        })

    const goToAssetLogs = (assetUuid: string) =>
        router.push({
            name: AssetRouteName.Logs,
            params: {
                ...route.params.value,
                [assetParamKey]: assetUuid,
            },
            query: route.query.value,
        })

    const goToAssetLogDetails = (logId: number) =>
        router.push({
            name: AssetRouteName.LogDetails,
            params: {
                ...route.params.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToAssetSecrets = (assetUuid?: string | null) =>
        router.push({
            name: AssetRouteName.Secrets,
            params: {
                ...route.params.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
            },
            query: route.query.value,
        })

    const goToAssetSecretCreate = (assetUuid?: string | null) => {
        router.push({
            name: AssetRouteName.SecretCreate,
            params: {
                ...route.params.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
            },
            query: route.query.value,
        })
    }

    const goToAssetSecretEdit = (secretUuid: string, assetUuid?: string | null) =>
        router.push({
            name: AssetRouteName.SecretEdit,
            params: {
                ...route.params.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
                [secretParamKey]: secretUuid,
            },
            query: route.query.value,
        })

    const goToAssetSecretDetails = (secretUuid: string) =>
        router.push({
            name: AssetRouteName.SecretDetails,
            params: {
                ...route.params.value,
                [secretParamKey]: secretUuid,
            },
            query: route.query.value,
        })

    const goToAssetSecretLogs = (secretUuid: string, assetUuid?: string | null) =>
        router.push({
            name: AssetRouteName.SecretLogs,
            params: {
                ...route.params.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
                [secretParamKey]: secretUuid,
            },
            query: route.query.value,
        })

    const goToAssetSecretLogDetails = (
        logId: number,
        secretUuid: string | null,
        assetUuid?: string | null,
        companyUuid?: string | null
    ) =>
        router.push({
            name: AssetRouteName.SecretLogDetails,
            params: {
                ...route.params.value,
                [companyParamKey]: companyUuid ?? route.companyUuid.value,
                [assetParamKey]: assetUuid ?? route.companyAssetUuid.value,
                [secretParamKey]: secretUuid ?? route.secretUuid.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToServiceCreate = () => {
        router.push({
            name: ServiceRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToServices = () =>
        router.push({
            name: ServiceRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToServiceClone = (uuid: string) =>
        router.push({
            name: ServiceRouteName.Clone,
            params: {
                ...route.params.value,
                [csParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToServiceEditRate = (uuid: string) =>
        router.push({
            name: ServiceRouteName.EditRate,
            params: {
                ...route.params.value,
                [csParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToServiceEdit = (uuid: string) =>
        router.push({
            name: ServiceRouteName.Edit,
            params: {
                ...route.params.value,
                [csParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToServiceLogs = (csUuid: string) =>
        router.push({
            name: ServiceRouteName.Logs,
            params: {
                ...route.params.value,
                [csParamKey]: csUuid,
            },
            query: route.query.value,
        })

    const goToServiceLogDetails = (logId: number, csUuid?: string | null) =>
        router.push({
            name: ServiceRouteName.LogDetails,
            params: {
                ...route.params.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToServiceUsages = (csUuid?: string | null) =>
        router.push({
            name: ServiceRouteName.Usage,
            params: {
                ...route.params.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
            },
            query: route.query.value,
        })

    const goToServiceInvoices = (csUuid?: string | null) =>
        router.push({
            name: ServiceRouteName.Invoices,
            params: {
                ...route.params.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
            },
            query: route.query.value,
        })

    const goToServiceInvoiceEdit = (invoiceUuid: string, csUuid?: string | null) =>
        router.push({
            name: ServiceRouteName.InvoiceEdit,
            params: {
                ...route.params.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
                [invoiceItemParamKey]: invoiceUuid,
            },
            query: route.query.value,
        })

    const goToServiceInvoiceLogs = (invoiceUuid: string, csUuid?: string | null) =>
        router.push({
            name: ServiceRouteName.InvoiceLogs,
            params: {
                ...route.params.value,
                [csParamKey]: csUuid ?? route.companyServiceUuid.value,
                [invoiceItemParamKey]: invoiceUuid,
            },
            query: route.query.value,
        })

    const goToServiceDetails = (uuid: string) =>
        router.push({
            name: ServiceRouteName.Details,
            params: {
                ...route.params.value,
                [csParamKey]: uuid,
            },
            query: route.query.value,
        })

    const goToTickets = () =>
        router.push({
            name: TicketRouteName.Index,
            query: route.query.value,
        })

    const goToTicketDetails = (ticketUuid: string) =>
        router.push({
            name: TicketRouteName.Details,
            params: {
                ...route.params.value,
                [ticketParamKey]: ticketUuid,
            },
            query: route.query.value,
        })

    const goToTicketEdit = (ticketUuid: string) =>
        router.push({
            name: TicketRouteName.Edit,
            params: {
                ...route.params.value,
                [ticketParamKey]: ticketUuid,
            },
            query: route.query.value,
        })

    const goToTicketLogs = (ticketUuid: string) =>
        router.push({
            name: TicketRouteName.Logs,
            params: {
                ...route.params.value,
                [ticketParamKey]: ticketUuid,
            },
            query: route.query.value,
        })

    const goToTicketLogDetails = (logId: number, ticketUuid?: string) =>
        router.push({
            name: TicketRouteName.LogDetails,
            params: {
                ...route.params.value,
                [ticketParamKey]: ticketUuid ?? route.ticketUuid.value,
                [logParamKey]: logId,
            },
            query: route.query.value,
        })

    const goToArticles = () =>
        router.push({
            name: ArticleRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToArticleCreate = () => {
        router.push({
            name: ArticleRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToArticleEdit = (articleUuid: string) =>
        router.push({
            name: ArticleRouteName.Edit,
            params: {
                ...route.params.value,
                [articleParamKey]: articleUuid,
            },
            query: route.query.value,
        })

    const goToArticleDetails = (articleUuid: string) => {
        router.push({
            name: ArticleRouteName.Show,
            params: {
                ...route.params.value,
                [articleParamKey]: articleUuid,
            },
            query: route.query.value,
        })
    }

    const goToAdminTenants = () =>
        router.push({
            name: AdminTenantsRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToAdminTenantCreate = () =>
        router.push({
            name: AdminTenantsRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })

    const goToAdminTenantEdit = (tenantUuid: string) =>
        router.push({
            name: AdminTenantsRouteName.Edit,
            params: {
                [tenantParamKey]: tenantUuid,
                ...route.params.value,
            },
            query: route.query.value,
        })

    const goToAdminTenantDetails = (tenantUuid: string) =>
        router.push({
            name: AdminTenantsRouteName.Details,
            params: {
                [tenantParamKey]: tenantUuid,
                ...route.params.value,
            },
            query: route.query.value,
        })

    const goToAdminTenantEmailTemp = (tenantUuid: string) =>
        router.push({
            name: AdminTenantsRouteName.Emails,
            params: {
                [tenantParamKey]: tenantUuid,
                ...route.params.value,
            },
            query: route.query.value,
        })

    const goToCompanyArticles = () =>
        router.push({
            name: CompanyArticleRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToCompanyArticleCreate = () => {
        router.push({
            name: CompanyArticleRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToCompanyArticleEdit = (articleUuid: string) =>
        router.push({
            name: CompanyArticleRouteName.Edit,
            params: {
                ...route.params.value,
                [articleParamKey]: articleUuid,
            },
            query: route.query.value,
        })

    const goToCompanyArticleDetails = (articleUuid: string) => {
        router.push({
            name: CompanyArticleRouteName.Show,
            params: {
                ...route.params.value,
                [articleParamKey]: articleUuid,
            },
            query: route.query.value,
        })
    }

    const goToProducts = () =>
        router.push({
            name: ProductRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToProductCreate = () => {
        router.push({
            name: ProductRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToProductEdit = (productUuid: string) =>
        router.push({
            name: ProductRouteName.Edit,
            params: {
                ...route.params.value,
                [productParamKey]: productUuid,
            },
            query: route.query.value,
        })

    const goToProductDetails = (productUuid: string) => {
        router.push({
            name: ProductRouteName.Details,
            params: {
                ...route.params.value,
                [productParamKey]: productUuid,
            },
            query: route.query.value,
        })
    }

    const goToBankFeeds = () =>
        router.push({
            name: BankFeedRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToBankFeedHistory = (bankFeedUuid: string) => {
        router.push({
            name: BankFeedRouteName.History,
            params: {
                ...route.params.value,
                [bankFeedParamKey]: bankFeedUuid,
            },
            query: route.query.value,
        })
    }

    const goToCategories = () => {
        const { parentUuid, ...queryParams } = route.query.value

        router.push({
            name: CategoryRouteName.Index,
            params: route.params.value,
            query: queryParams,
        })
    }

    const goToCategoryCreate = (parentUuid?: string) => {
        router.push({
            name: CategoryRouteName.Create,
            params: route.params.value,
            query: { parentUuid, ...route.query.value },
        })
    }

    const goToCategoryUpdate = (categoryUuid: string) =>
        router.push({
            name: CategoryRouteName.Edit,
            params: {
                ...route.params.value,
                [categoryParamKey]: categoryUuid,
            },
            query: route.query.value,
        })

    const goToTicketTypes = () => {
        router.push({
            name: TicketTypeRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToTicketTypeCreate = () => {
        router.push({
            name: TicketTypeRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToTicketTypeUpdate = (ticketTypeUuid: string) =>
        router.push({
            name: TicketTypeRouteName.Edit,
            params: {
                ...route.params.value,
                [ticketTypeParamKey]: ticketTypeUuid,
            },
            query: route.query.value,
        })

    const goToTicketPriorities = () => {
        router.push({
            name: TicketPriorityRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToTicketPriorityCreate = () => {
        router.push({
            name: TicketPriorityRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToTicketPriorityUpdate = (ticketPriorityUuid: string) => {
        router.push({
            name: TicketPriorityRouteName.Edit,
            params: {
                ...route.params.value,
                [ticketPriorityParamKey]: ticketPriorityUuid,
            },
            query: route.query.value,
        })
    }

    const goToTicketDepartments = () => {
        router.push({
            name: TicketDepartmentRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToTicketDepartmentCreate = () => {
        router.push({
            name: TicketDepartmentRouteName.Create,
            params: route.params.value,
            query: route.query.value,
        })
    }

    const goToTicketDepartmentUpdate = (ticketDepartmentUuid: string) => {
        router.push({
            name: TicketDepartmentRouteName.Edit,
            params: {
                ...route.params.value,
                [ticketDepartmentParamKey]: ticketDepartmentUuid,
            },
            query: route.query.value,
        })
    }

    const goToReceivables = () =>
        router.push({
            name: ReceivableRouteName.Index,
            params: route.params.value,
            query: route.query.value,
        })

    const goToReceivableNotify = (customerId: string) => {
        router.push({
            name: ReceivableRouteName.Notify,
            params: {
                ...route.params.value,
                customerId: customerId,
            },
            query: route.query.value,
        })
    }

    const back = () => router.back()

    return {
        back,
        goToCompanyAssetCreate,
        goToCompanyAssetDetails,
        goToCompanyAssetEdit,
        goToCompanyAssetLogDetails,
        goToCompanyAssetLogs,
        goToCompanyAssetSecretCreate,
        goToCompanyAssetSecretDetails,
        goToCompanyAssetSecretEdit,
        goToCompanyAssetSecretLogDetails,
        goToCompanyAssetSecretLogs,
        goToCompanyAssetSecrets,
        goToCompanyAssets,
        goToCompanyBillingProduct,
        goToCompanyBillingMultiple,
        goToCompanyBillingMultipleSend,
        goToCompanyBillingSingle,
        goToCompanyBillingCombine,
        goToCompanyBillingCombineEditInvoiceItem,
        goToCompanyBillingCombineSend,
        goToCompanyContactCreate,
        goToCompanyContactDetails,
        goToCompanyContactEdit,
        goToCompanyContactLogDetails,
        goToCompanyContactLogs,
        goToCompanyContactResetPassword,
        goToCompanyContacts,
        goToCompanyDashboard,
        goToCompanyInvoices,
        goToCompanyInvoiceEdit,
        goToCompanyInvoiceLogs,
        goToCompanyInvoiceLogDetails,
        goToCompanyInvoiceDetails,
        goToCompanyInvoiceSend,
        goToCompanyInvoiceItemEdit,
        goToCompanyInvoiceItemLogs,
        goToCompanyInvoiceItemLogDetails,
        goToCompanyInvoicePayments,
        goToCompanyInvoicePaymentDetails,
        goToCompanyLocationCreate,
        goToCompanyLocationEdit,
        goToCompanyLocationLogs,
        goToCompanyLocationLogDetails,
        goToCompanyLocations,
        goToCompanyLogs,
        goToCompanyLogDetails,
        goToCompanyPay,
        goToCompanyPayEditInvoice,
        goToCompanyPaySendReceipt,
        goToCompanyReceipts,
        goToCompanyReceiptDetails,
        goToCompanyReceiptEdit,
        goToCompanyReceiptLogs,
        goToCompanyReceiptLogDetails,
        goToCompanyReceiptSend,
        goToCompanyReceiptItemEdit,
        goToCompanyReceiptItemLogs,
        goToCompanyReceiptItemLogDetails,
        goToCompanySecretLogDetails,
        goToCompanySecretLogs,
        goToCompanySecrets,
        goToCompanySecretsCreate,
        goToCompanySecretDetails,
        goToCompanySecretEdit,
        goToCompanySendEmail,
        goToCompanyServices,
        goToCompanyServiceCreate,
        goToCompanyServiceDetails,
        goToCompanyCompanyServiceUsageDetails,
        goToCompanyServiceLogs,
        goToCompanyServiceLogDetails,
        goToCompanyServiceInvoices,
        goToCompanyServiceInvoiceEdit,
        goToCompanyServiceInvoiceLogs,
        goToCompanyServiceUsages,
        goToCompanyServiceEdit,
        goToCompanyServiceEditRate,
        goToCompanyServiceClone,
        goToCompanyStatements,
        goToCompanyStatementNotes,
        goToCompanyTickets,
        goToCompanyTicketEdit,
        goToCompanyTicketLogs,
        goToCompanyTicketLogDetails,
        goToAdminStaffCreate,
        goToAdminStaffs,
        goToAdminStaffEdit,
        goToAdminStaffActivities,
        goToAdminStaffActivityDetails,
        goToCompanyCreate,
        goToCompanies,
        goToContacts,
        goToContactCreate,
        goToContactDetails,
        goToContactLogs,
        goToContactLogDetails,
        goToContactResetPassword,
        goToContactUpdate,
        goToLocationCreate,
        goToLocations,
        goToLocationEdit,
        goToLocationLogs,
        goToLocationLogDetails,
        goToAssets,
        goToAssetCreate,
        goToAssetLogs,
        goToAssetEdit,
        goToAssetDetails,
        goToAssetLogDetails,
        goToAssetSecrets,
        goToAssetSecretCreate,
        goToAssetSecretEdit,
        goToAssetSecretDetails,
        goToAssetSecretLogs,
        goToServiceCreate,
        goToServices,
        goToServiceClone,
        goToServiceEditRate,
        goToServiceEdit,
        goToServiceLogs,
        goToServiceLogDetails,
        goToServiceUsages,
        goToServiceInvoices,
        goToServiceInvoiceEdit,
        goToServiceInvoiceLogs,
        goToServiceDetails,
        goToTickets,
        goToTicketDetails,
        goToTicketEdit,
        goToTicketLogs,
        goToTicketLogDetails,
        goToArticles,
        goToArticleCreate,
        goToArticleEdit,
        goToArticleDetails,
        goToAdminTenants,
        goToAdminTenantCreate,
        goToAdminTenantEdit,
        goToAdminTenantDetails,
        goToAdminTenantEmailTemp,
        goToCompanyArticles,
        goToCompanyArticleCreate,
        goToCompanyArticleEdit,
        goToCompanyArticleDetails,
        goToProducts,
        goToProductCreate,
        goToProductEdit,
        goToProductDetails,
        goToBankFeeds,
        goToBankFeedHistory,
        goToCategories,
        goToCategoryCreate,
        goToCategoryUpdate,
        goToTicketTypes,
        goToTicketTypeCreate,
        goToTicketTypeUpdate,
        goToTicketPriorities,
        goToTicketPriorityCreate,
        goToTicketPriorityUpdate,
        goToTicketDepartments,
        goToTicketDepartmentCreate,
        goToTicketDepartmentUpdate,
        goToReceivables,
        goToReceivableNotify,
    }
}

export const useAppRouter = () => useRouter()
